import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import IN from './Components/IN/IN';
import TE from './Components/TE/TE';
import SE from './Components/SE/SE';
import NonSEButtons from './Components/ButtonArea/NonSE';
import SEButtons from './Components/ButtonArea/SE';
import CBModal from './Components/IN/CustomFields/Checkbox/Modal/Modal';
import TandCModal from './Components/IN/CustomFields/TandC/Modal';
import SmsVercode from './Components/IN/ProfileFields/SmsVercode';
import { setFv } from '/src/Redux/actions';
import { loadLiff } from '/src/Util/Liff';
import { FormProvider } from './Contexts/FormContext';
import PasswordFields from './Components/IN/PasswordFields';
import { initializeGlobalOpenAlert, debug_log } from './Util/Util';
import HookSessionFeeSelect from './Components/ControllerSpecific/HookEvents/HookSessionFeeSelect';
import HookEventFriendInputs from './Components/ControllerSpecific/HookEvents/FriendList/FriendInputs';
import Turnstile from '../../react_common/src/Components/Turnstile';
import mergeFormInputsCtaStyle from './Util/mergeFormInputsCtaStyle';

// Initialize global utilities
initializeGlobalOpenAlert();

function resolvePreviewFormStep(isPreview, num_se) {
	if (!isPreview) return 1;
	try {
		const raw = parseInt(new URLSearchParams(window.location.search).get('form_step') || '', 10);
		if (!Number.isFinite(raw) || raw < 1) return 1;
		if (num_se > 0) return Math.min(raw, num_se);
		return raw;
	} catch (_e) {
		return 1;
	}
}

const App = (props) => {
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();
	const turnstileRef = useRef(null);
	const normalize_fieldname = (form_component) => {
		if (!form_component || typeof form_component !== 'object') {
			return form_component;
		}
		if (form_component.fieldname) {
			return form_component;
		}
		const componentId = String(form_component._id || '');
		if (form_component.type === 'PCU' && componentId) {
			return {
				...form_component,
				fieldname: `cust_${componentId}`
			};
		}
		return form_component;
	};

	const {page_type, csrf_token, acct_id, funnel_page_id, hook_status, preview, sl, internaltest, controller, curlang: curlangProp} = props;
	const previewLang = (() => {
		const fromProp = String(curlangProp || '').trim();
		if (fromProp === 'tc' || fromProp === 'sc' || fromProp === 'en') {
			return fromProp;
		}
		try {
			const fromQuery = String(new URLSearchParams(window.location.search).get('lang') || '').trim();
			if (fromQuery === 'tc' || fromQuery === 'sc' || fromQuery === 'en') {
				return fromQuery;
			}
		} catch (_e) {}
		return '';
	})();
	const [formFields, setFormFields] = useState({
		user_data: {},
		page: {},
		profile_fields: {},
		form_components: [],
		num_se: 0,
		turnstile_site_key: ''
	});
	const [loaded, setLoaded] = useState(false);
	const [spamProtection, setSpamProtection] = useState({
		honeypot_enabled: false,
		honeypot_field: '',
		honeypot_autocomplete: 'new-password',
		rate_limited: false
	});
	const honeypotRef = useRef(null);
	const [turnstileRequired, setTurnstileRequired] = useState(false);
	const formLoadedAtRef = useRef(Math.floor(Date.now() / 1000));

	const lineUserData = useSelector(state => state.lineUserData);
	const turnstileToken = useSelector(state => state.turnstileToken);
	const curStep = useSelector(state => state.curStep);
	const iframeMode = (() => {
		try {
			return window.self !== window.top ? 1 : 0;
		} catch (e) {
			return 1;
		}
	})();
	const isWeChatBrowser = typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent || '');
	const isTurnstileCapable = controller.indexOf('Shopcarts') === -1 && !(preview === true || preview === '1') && iframeMode === 0 && !isWeChatBrowser;
	const showHoneypot = isTurnstileCapable && spamProtection.honeypot_enabled && Boolean(spamProtection.honeypot_field) && !isWeChatBrowser;
	const showTurnstileWidget = isTurnstileCapable && turnstileRequired;
	const isIgnorableTurnstile401 = (error) => {
		return Boolean(window.ignoreTurnstileErrors && error?.response?.status === 401);
	};
	const isPreviewMode = preview === true || preview === '1' || preview === 1;

	useEffect(() => {
		if (!isPreviewMode || !iframeMode) return;
		try {
			window.parent.postMessage({ type: 'xeditor_preview_form_step', step: curStep }, '*');
		} catch (_e) {}
	}, [curStep, isPreviewMode, iframeMode]);

	useEffect(() => {
		window.__FORM_DEBUG__ = Boolean(preview === true || preview === '1' || internaltest);
		// Tell backend this request is coming from an iframe.
		if (iframeMode) {
			if (axios.defaults.headers && axios.defaults.headers.common) {
				axios.defaults.headers.common['X-Iframe'] = '1';
			}
		} else if (axios.defaults.headers && axios.defaults.headers.common) {
			delete axios.defaults.headers.common['X-Iframe'];
		}

		// Wait for i18n to be initialized before changing language
		i18n.changeLanguage(props.lang);

		const payload = {
			csrf_token: csrf_token,
			acct_id: acct_id,
			funnel_page_id: funnel_page_id,
			hook_status: hook_status,
			preview: preview,
			internaltest: internaltest,
			iframe: iframeMode,
			controller: controller
		}
		
		// Set page data to Redux store so components can access acct_id
		dispatch({type: 'SET_PAGE_DATA', payload: payload});
		
		// Load data directly without global interceptors
		loadData();

		if (!preview) {
			loadLiff(dispatch, csrf_token, page_type, funnel_page_id, null, sl, preview);
		}
	}, []);

	useEffect(() => {
		window.__form_turnstile_required__ = turnstileRequired;
		window.__set_turnstile_required__ = () => setTurnstileRequired(true);
		return () => {
			try {
				delete window.__form_turnstile_required__;
				delete window.__set_turnstile_required__;
			} catch (e) {}
		};
	}, [turnstileRequired]);

	useEffect(() => {
		dispatch({
			type: 'SET_PAGE_DATA',
			payload: {
				csrf_token,
				acct_id,
				funnel_page_id,
				hook_status,
				preview,
				internaltest,
				iframe: iframeMode,
				controller,
				turnstile_required: turnstileRequired
			}
		});
	}, [turnstileRequired]);

	useEffect(() => {
		if (!showHoneypot) {
			try {
				delete window.__clear_form_honeypot__;
			} catch (e) {}
			return;
		}
		const clearHoneypotValue = () => {
			if (honeypotRef.current) {
				honeypotRef.current.value = '';
			}
		};
		window.__clear_form_honeypot__ = clearHoneypotValue;
		const timer = setTimeout(clearHoneypotValue, 500);
		const form = document.getElementById('reg-form');
		const onSubmit = () => {
			clearHoneypotValue();
		};
		form?.addEventListener('submit', onSubmit, true);
		return () => {
			clearTimeout(timer);
			form?.removeEventListener('submit', onSubmit, true);
			try {
				delete window.__clear_form_honeypot__;
			} catch (e) {}
		};
	}, [showHoneypot, spamProtection.honeypot_field]);

	useEffect(() => {
		window.__form_honeypot_field__ = spamProtection.honeypot_field || '';
		return () => {
			try {
				delete window.__form_honeypot_field__;
			} catch (e) {}
		};
	}, [spamProtection.honeypot_field]);

	// Expose a global helper for AJAX submission flows (CheckFormHooks)
	useEffect(() => {
		if (!showTurnstileWidget) {
			try {
				delete window.__ensure_turnstile_token__;
			} catch (e) {}
			return;
		}
		window.__ensure_turnstile_token__ = async () => {
			const form = document.getElementById('reg-form');
			if (!turnstileRef.current || typeof turnstileRef.current.execute !== 'function') {
				return null;
			}
			try {
				const token = await turnstileRef.current.execute(10000);
				if (typeof token === 'string' && token.length > 10 && !token.startsWith('TEMP_')) {
					return token;
				}
				const fallback = form?.dataset?.turnstileToken;
				if (typeof fallback === 'string' && fallback.length > 10 && !fallback.startsWith('TEMP_')) {
					return fallback;
				}
				return null;
			} catch (e) {
				return null;
			}
		};
		return () => {
			try {
				delete window.__ensure_turnstile_token__;
			} catch (e) {}
		};
	}, [showTurnstileWidget]);

	// Modify the loadData function to handle Turnstile initialization
	async function loadData() {
		let bootstrapResponseInterceptorId = null;
		try {
			// Set a flag to allow form to continue loading despite 401 errors
			window.ignoreTurnstileErrors = true;
			
			// Create Axios interceptors to handle authorization errors silently
			bootstrapResponseInterceptorId = axios.interceptors.response.use(
				response => response,
				error => {
					if (isIgnorableTurnstile401(error)) {
						return Promise.resolve({ 
							data: error.response.data || {},
							status: 200,
							statusText: 'OK (Recovered from 401)',
							headers: error.response.headers,
							config: error.config,
							_wasTurnstileError: true
						});
					}
					return Promise.reject(error);
				}
			);
			
			let url = `/FormComponents/ajaxGetData/${page_type}/${acct_id}`;
			const query = new URLSearchParams();
			if (page_type === 'funnel' && funnel_page_id) {
				query.set('funnel_page_id', funnel_page_id);
			}
			if (previewLang) {
				query.set('lang', previewLang);
			}
			const queryString = query.toString();
			if (queryString) {
				url += `/?${queryString}`;
			}
			const response = await axios.get(url);

			if (response.data.cta_style) {
				mergeFormInputsCtaStyle(document.getElementById('form-inputs'), response.data.cta_style);
			}

			// Process response data, ensuring we have defaults for missing data
			const normalized_form_components = (response.data.form_components || []).map((form_component) => normalize_fieldname(form_component));
			const formData = {
				page: response.data.page || {},
				profile_fields: response.data.profile_fields || {},
				form_components: normalized_form_components,
				num_se: response.data.num_se || 0,
				turnstile_site_key: response.data.turnstile_site_key ? 
					String(response.data.turnstile_site_key) : ''
			};

			// Set the form fields with turnstile key
			setFormFields(prevData => ({
				...prevData,
				...formData
			}));
			const sp = response.data.spam_protection || {};
			setSpamProtection({
				honeypot_enabled: Boolean(sp.honeypot_enabled),
				honeypot_field: sp.honeypot_field || '',
				honeypot_autocomplete: sp.honeypot_autocomplete || 'new-password',
				rate_limited: Boolean(sp.rate_limited)
			});
			if (sp.rate_limited) {
				setTurnstileRequired(true);
			}

			const form_components = formData.form_components;

			// Only dispatch these if we have the data
			if (!response._wasTurnstileError) {
				// Preview (incl. xeditor embed) does not load FormValidation.io — skip FV init
				if (!isPreviewMode) {
					dispatch(setFv(formData.num_se, hook_status));
				}
				const initialStep = resolvePreviewFormStep(isPreviewMode, formData.num_se);
				if (initialStep > 1) {
					dispatch({ type: 'SET_CUR_STEP', payload: initialStep });
					dispatch({ type: 'SET_NEXT_STEP', payload: Math.min(initialStep + 1, formData.num_se || initialStep + 1) });
				}

				const user_data = response.data.user_data || {};
				dispatch({type: 'SET_USER_DATA', payload: user_data});

				dispatch({type: 'SET_CONTACT_FIELD', payload: {
					field: 'email',
					value: user_data ? user_data.email : ''
				}});

				dispatch({type: 'SET_CONTACT_FIELD', payload: {
					field: 'country_code',
					value: user_data ? user_data.country_code : ''
				}});

				dispatch({type: 'SET_CONTACT_FIELD', payload: {
					field: 'mobile',
					value: user_data ? user_data.mobile : ''
				}});

				const optionFields = [];
				let currentSEIndex = 0;
				form_components.forEach(form_component => {
					if (form_component.format === 'SE') {
						currentSEIndex++;
					}
					if (['select', 'radio', 'checkbox'].includes(form_component.input_type)) {
						const existingValue = user_data ? user_data[form_component.fieldname] : '';
						optionFields.push({
							fieldname: form_component.fieldname,
							value: existingValue || '',
							seq: form_component.seq
						});
					}
				});
				
				if (optionFields.length > 0) {
					dispatch({type: 'SET_OPTION_FIELDS', payload: optionFields});
				}
			}
			
			// After a delay, stop ignoring Turnstile errors for future API calls
			setTimeout(() => {
				window.ignoreTurnstileErrors = false;
			}, 1500);
			
		} catch (e) {
			if (!isIgnorableTurnstile401(e)) {
				console.error('Error loading form data:', e);
			}

			// Always continue with the form load, even if there was an error
			// This ensures the user can still use the form even if some API calls failed
			// Set minimal form data to ensure UI doesn't break
			setFormFields(prevData => ({
				...prevData,
				page: prevData.page || {},
				profile_fields: prevData.profile_fields || {},
				form_components: prevData.form_components || [],
				num_se: prevData.num_se || 0
			}));
		} finally {
			setLoaded(true);
			
			// Notify that React form is loaded for FormValidation
			setTimeout(() => {
				const event = new CustomEvent('reactFormLoaded', { 
					detail: { 
						controller: controller,
						formFields: formFields 
					} 
				});
				document.dispatchEvent(event);
			}, 100);

			if (bootstrapResponseInterceptorId !== null) {
				axios.interceptors.response.eject(bootstrapResponseInterceptorId);
			}
			
			// Register a global error handler for AJAX errors
			if (window.$ && window.$.ajaxError) {
				$(document).off('ajaxError.turnstileIgnore').on('ajaxError.turnstileIgnore', function(event, jqXHR) {
					if (jqXHR.status === 401) {
						if (window.ignoreTurnstileErrors) {
							event.preventDefault();
							return false;
						}
					}
				});
			}
		}
	}

	useEffect(() => {
		if (!showTurnstileWidget) {
			return;
		}

		const syncTurnstileTokenToForm = (form, token) => {
			if (!form || !token) {
				return;
			}
			form.dataset.turnstileToken = token;
			form.dataset.turnstileTimestamp = Date.now().toString();
			const tokenFieldNames = [
				'cf-turnstile-response',
				'turnstile-response',
				'cf-turnstile-token',
				'g-recaptcha-response',
				'token',
				'cf_turnstile_response',
				'turnstile_response',
				'cf_challenge_response'
			];
			tokenFieldNames.forEach((fieldName) => {
				let inputField = form.querySelector(`input[name="${fieldName}"]`);
				if (!inputField) {
					inputField = document.createElement('input');
					inputField.type = 'hidden';
					inputField.name = fieldName;
					form.appendChild(inputField);
				}
				inputField.value = token;
			});
			window.turnstileToken = token;
		};
		
		const ensureFreshTokenOrAlert = (form) => {
			if (!turnstileRef.current || typeof turnstileRef.current.execute !== 'function') {
				if (typeof window.openAlert === 'function') {
					window.openAlert('安全驗證尚未就緒，請稍後再試。');
				}
				return Promise.resolve(false);
			}
			window.turnstileToken = '';
			return turnstileRef.current.execute(10000).then((newToken) => {
				const token = newToken || form?.dataset?.turnstileToken;
				if (token && typeof token === 'string' && token.length > 10 && !token.startsWith('TEMP_')) {
					syncTurnstileTokenToForm(form, token);
					return true;
				}
				if (typeof window.openAlert === 'function') {
					window.openAlert('Please verify you are not a robot');
				}
				return false;
			}).catch((e) => {
				if (typeof window.openAlert === 'function') {
					window.openAlert('Please verify you are not a robot');
				}
				return false;
			});
		};

		// Intercept native submit to guarantee token exists before POST
		const form = document.getElementById('reg-form');
		if (form && !form._turnstileSubmitIntercepted) {
			form._turnstileSubmitIntercepted = true;
			form._turnstileSubmitting = false;
			if (!form._originalSubmit) {
				form._originalSubmit = form.submit;
				form.submit = function() {
					if (form._turnstileSubmitting) {
						return HTMLFormElement.prototype.submit.call(form);
					}
					form._turnstileSubmitting = true;
					return ensureFreshTokenOrAlert(form).then((ok) => {
						if (!ok) {
							return;
						}
						HTMLFormElement.prototype.submit.call(form);
					}).finally(() => {
						setTimeout(() => {
							form._turnstileSubmitting = false;
						}, 1000);
					});
				};
			}
			form.addEventListener('submit', function(e) {
				if (form._turnstileSubmitting) {
					return;
				}
				e.preventDefault();
				ensureFreshTokenOrAlert(form).then((ok) => {
					if (!ok) {
						return;
					}
					form._turnstileSubmitting = true;
					try {
						HTMLFormElement.prototype.submit.call(form);
					} finally {
						setTimeout(() => {
							form._turnstileSubmitting = false;
						}, 1000);
					}
				});
			}, true);
		}

		// Monkey patch form submission to ensure Turnstile validation
		if (window.$ && window.$.fn && window.$.fn.submit) {
			const originalSubmit = $.fn.submit;
			
			$.fn.submit = function(arg) {
				// If this is the registration form and it's a direct submit without arguments
				if (this.attr('id') === 'reg-form' && typeof arg === 'undefined') {
					const form = this[0];
					debug_log('Submitting form requires fresh Turnstile token');
					return ensureFreshTokenOrAlert(form).then((ok) => {
						if (ok) {
							originalSubmit.apply(this, [arg]);
						}
					});
				}
				
				// For all other cases, proceed with original submit
				return originalSubmit.apply(this, arguments);
			};
			
			debug_log('Form submit method patched for Turnstile pre-submit execute');
		}
	}, [showTurnstileWidget]);

	useEffect(() => {
		if (!showTurnstileWidget) {
			return;
		}
		
		// Configure axios to always include the token
		const requestInterceptorId = axios.interceptors.request.use((config) => {
			// Get the token from Redux store or window global
			let tokenFromState = null;

			// Case 1: Redux state is already a string token
			if (typeof turnstileToken === 'string') {
				if (turnstileToken.length > 10 && !turnstileToken.startsWith('TEMP_')) {
					tokenFromState = turnstileToken;
				}
			} else if (turnstileToken && typeof turnstileToken === 'object') {
				// Case 2: Object with token / response property
				if (typeof turnstileToken.token === 'string' && turnstileToken.token.length > 10 && !turnstileToken.token.startsWith('TEMP_')) {
					tokenFromState = turnstileToken.token;
				} else if (typeof turnstileToken.response === 'string' && turnstileToken.response.length > 10 && !turnstileToken.response.startsWith('TEMP_')) {
					tokenFromState = turnstileToken.response;
				}
			}

			const token = tokenFromState || window.turnstileToken;
			if (token) {
				// Add token to request headers
				config.headers['X-Turnstile-Token'] = token;
				
				// If this is a form submission request, ensure token is in the form data
				if (config.headers['Content-Type'] === 'application/x-www-form-urlencoded' && 
					typeof config.data === 'string') {
					
					// Check if the request already contains any of the token field names
					const tokenFieldNames = [
						'cf-turnstile-response', 
						'turnstile-response', 
						'cf-turnstile-token',
						'g-recaptcha-response'
					];
					
					let hasToken = false;
					for (const fieldName of tokenFieldNames) {
						if (config.data.includes(fieldName + '=')) {
							hasToken = true;
							break;
						}
					}
					
					// If token is not in the data, add it
					if (!hasToken) {
						config.data += '&cf-turnstile-response=' + encodeURIComponent(token);
					}
				}
			}
			
			return config;
		}, (error) => {
			return Promise.reject(error);
		});

		return () => {
			axios.interceptors.request.eject(requestInterceptorId);
		};
	}, [turnstileToken, showTurnstileWidget]);

	useEffect(() => {
		if (!loaded) {
			return;
		}
		const form = document.getElementById('reg-form');
		if (!form) {
			return;
		}
		const ensure_select_name_attr = () => {
			const fields = form.querySelectorAll('select[id^="cust_"], select.SB, select[data-role="SB"]');
			fields.forEach((field) => {
				if (!field.getAttribute('name') && field.id) {
					field.setAttribute('name', field.id);
				}
			});
		};
		ensure_select_name_attr();
		const observer = new MutationObserver(() => {
			ensure_select_name_attr();
		});
		observer.observe(form, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['name', 'id', 'class']
		});
		return () => {
			observer.disconnect();
		};
	}, [loaded]);

	if (!loaded) {
		return <div className="d-flex align-items-center justify-content-center" style={{height: '100vh'}}>
			<div className="spinner-border text-primary me-2" role="status" aria-hidden="true">
				<span className="visually-hidden">Loading...</span>
			</div>
			<span>Loading...</span>
		</div>;
	}

	return (
		<>
		<FormProvider options={{ submit_text: formFields.page?.cta_text || t('提交') }}>
		{showHoneypot && (
			<>
				<input
					key={spamProtection.honeypot_field}
					type="text"
					name={spamProtection.honeypot_field}
					defaultValue=""
					tabIndex={-1}
					readOnly
					ref={honeypotRef}
					autoComplete={spamProtection.honeypot_autocomplete}
					autoCorrect="off"
					autoCapitalize="off"
					spellCheck={false}
					data-lpignore="true"
					data-1p-ignore="true"
					data-bwignore="true"
					data-form-type="other"
					aria-hidden="true"
					onFocus={(e) => { e.target.readOnly = false; }}
					style={{position: 'absolute', left: '-9999px', height: 0, width: 0, overflow: 'hidden'}}
				/>
				<input type="hidden" name="form_loaded_at" value={formLoadedAtRef.current} />
			</>
		)}
		{showTurnstileWidget && (
			<Turnstile
				ref={turnstileRef}
				siteKey={formFields.turnstile_site_key}
				formId="reg-form"
				autoExecute={false}
				renderOnLoad={true}
				onVerify={(token) => {
					dispatch({
						type: 'SET_TURNSTILE_TOKEN',
						payload: {
							token,
							timestamp: Date.now()
						}
					});
					const form = document.getElementById('reg-form');
					if (form) {
						form.dataset.turnstileToken = token;
						form.dataset.turnstileTimestamp = Date.now();
						const tokenFieldNames = [
							'cf-turnstile-response',
							'turnstile-response',
							'cf-turnstile-token',
							'g-recaptcha-response',
							'token',
							'cf_turnstile_response',
							'turnstile_response',
							'cf_challenge_response'
						];
						tokenFieldNames.forEach(fieldName => {
							let inputField = form.querySelector(`input[name="${fieldName}"]`);
							if (!inputField) {
								inputField = document.createElement('input');
								inputField.type = 'hidden';
								inputField.name = fieldName;
								form.appendChild(inputField);
							}
							inputField.value = token;
						});
					}
					window.turnstileToken = token;
					if (axios.defaults.headers) {
						axios.defaults.headers.common['X-Turnstile-Token'] = token;
					}
					const event = new CustomEvent('turnstileReady', { detail: { token } });
					document.dispatchEvent(event);
				}}
			/>
		)}
		
		{/* Other form fields */}
		{isTurnstileCapable && (
			<>
				<input type="hidden" name="_csrfToken" value={csrf_token} />
				<input type="hidden" name="line[user_id]" value={lineUserData.userId} />
				<input type="hidden" name="line[follow]" value={lineUserData.follow ? 1 : 0} />
				<input type="hidden" name="line[email]" value={lineUserData.email} />
				<input type="hidden" name="line[nickname]" value={lineUserData.nickname} />	
			</>
		)}
		<div className="merge-form">
			{(() => {
				const components = [];
				let currentSEDiv = null;
				let se_count = 0, in_count = 0;


				formFields.form_components.forEach((form_component, index) => {
					switch (form_component.format) {
						case 'SE':
							// Close the previous SE div if it exists
							if (currentSEDiv) {
								components.push(currentSEDiv);
							}

							// Start a new SE component
							se_count++;
							const div_class = se_count === 1 ? 'js-step js-step-active' : 'js-step';
							//console.log(`content`, form_component.content);

							// Create children array with HookSessionFeeSelect at the top for HookEvents
							const seChildren = [];
							if (controller === 'HookEvents') {
								// Always render HookSessionFeeSelect, let it handle the display logic internally
								seChildren.push(
									<HookSessionFeeSelect
										key={`hook-event-hook-session-fee-select-first`}
										displayIndex={0}
										funnel_page_id={funnel_page_id}
										lang={props.lang}
									/>
								);
							}

							currentSEDiv = (
								<SE
									key={`step-${se_count}`}
									lang={props.lang}
									formFields={formFields}
                                    se_count={se_count}
                                    form_component={form_component}
								>
									{seChildren}
								</SE>
							);
							break;

						case 'IN':
						case 'TE':
							if (form_component.format === 'IN') {
								++in_count;
							}

							// If no SE component exists, create a default container
							if (!currentSEDiv) {
								currentSEDiv = (
									<div className="default-container" key="default-container">
										<div className="quest_list fixtop tab-on">
											<p className="guide">Default Section</p>
										</div>
									</div>
								);
							}
							//console.log(`currentSEDiv`, currentSEDiv);

							// Ensure children is an array
							const childrenArray = React.Children.toArray(currentSEDiv.props.children);

							// Add IN and TE components to the current SE component
							switch (form_component.format) {
								case 'IN':
									currentSEDiv = React.cloneElement(
										currentSEDiv,
										{},
										[
											...childrenArray,
											<IN
												key={`in-${index}`}
												lang={props.lang}
												formFields={formFields}
												setFormFields={setFormFields}
												form_component={form_component}
												count={in_count}
											/>
										]
									);
									break;

								case "TE":
									currentSEDiv = React.cloneElement(
										currentSEDiv,
										{},
										[
											...childrenArray,
											<TE
												key={`te-${index}`}
												lang={props.lang}
												formFields={formFields}
												setFormFields={setFormFields}
												form_component={form_component}
											/>
										]
									);
									break;
							}
							
							//console.log(`currentSEDiv after`, currentSEDiv);
							break;
					}
				});

				// Push the last SE component or default container if it exists and append SMS and password fields inside it
				if (currentSEDiv) {
					// Collect additional elements
					const additionalElements = [];

					switch (controller) {
						case 'HookEvents':
							additionalElements.push(
								<HookEventFriendInputs
									key={`hook-event-friend-input`}
									lang={props.lang}
									funnel_page_id={funnel_page_id}
									formFields={formFields}
								/>
							);
							break;
					}

					formFields.form_components.forEach((form_component, index) => {
						if (form_component.fieldname === 'mobile' && form_component.sms_vercode) {
							additionalElements.push(
								<SmsVercode key={`sms-${index}`}
									lang={props.lang}
									formFields={formFields}
									form_component={form_component}
								/>
							);
						}
					});
					if (page_type === 'membership') {
						++in_count;
						additionalElements.push(
							<PasswordFields
								key={`password-${in_count}`}
								lang={props.lang}
								formFields={formFields}
								count={in_count}
								page={formFields.page}
							/>
						);
					}
					const finalChildren = React.Children.toArray(currentSEDiv.props.children).concat(additionalElements);
					components.push(
						React.cloneElement(
							currentSEDiv,
							{},
							finalChildren
						)
					);
				}

				return components;
			})()}
		</div>
		{controller.indexOf('Shopcarts') === -1 && (
			<>
				{formFields.num_se <= 1 ? (
					<NonSEButtons
						lang={props.lang}
						formFields={formFields}
					/>
				) : (
					<SEButtons
						lang={props.lang}
						formFields={formFields}
					/>
				)}
			</>
		)}
		
		<CBModal
			lang={props.lang}
		/>
		<TandCModal
			lang={props.lang}
		/>
		</FormProvider>
		</>
	);
}

export default App;